import { useForm, type UseFormReturn } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import type { PlantProfile, PlantProfileSuggestion } from "@/lib/api/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { usePreferences } from "@/lib/preferences";
import {
  ecFromMsCm,
  ecToMsCm,
  tempFromCelsius,
  tempToCelsius,
  type ECUnit,
  type TemperatureUnit,
} from "@/lib/units";
import { EnumSelect } from "@/components/enum-select";
import {
  FEEDER_CLASS_OPTIONS,
  PLANT_CATEGORY_OPTIONS,
  PLANT_STAGE_OPTIONS,
} from "@/lib/catalog-options";
import { useSuggestPlantProfile } from "@/lib/api/plant-profiles";
import { ApiError } from "@/lib/api/client";
import { applyChemistryRules, preserveOrConvert } from "@/pages/grows/form";

// PlantProfileForm mirrors the grow form's chemistry section: numeric windows
// are kept as strings in form state and parsed at submit. A profile is a
// reusable target preset; a grow snapshots one at create time.

const optionalNumString = z
  .string()
  .optional()
  .refine(
    (v) => v === undefined || v === "" || !Number.isNaN(Number(v)),
    "must be a number",
  );

const plantProfileObject = z.object({
  name: z.string().min(1, "name is required"),
  species: z.string().min(1, "species is required"),
  category: z.string().optional(),
  stage: z.string().optional(),
  feeder_class: z.string().optional(),
  ph_min: optionalNumString,
  ph_ideal: optionalNumString,
  ph_max: optionalNumString,
  ec_min: optionalNumString,
  ec_ideal: optionalNumString,
  ec_max: optionalNumString,
  temp_min_c: optionalNumString,
  temp_ideal_c: optionalNumString,
  temp_max_c: optionalNumString,
  notes: z.string().optional(),
  reason: z.string().optional(),
});

export type PlantProfileFormValues = z.infer<typeof plantProfileObject>;

// Chemistry windows are stored/edited in DISPLAY units, so the bounds/ordering
// rules (shared with the grow form) validate in those units per the active
// preference and produce naturally-worded messages.
export function makePlantProfileSchema(
  tempUnit: TemperatureUnit,
  ecUnit: ECUnit,
) {
  return applyChemistryRules(plantProfileObject, tempUnit, ecUnit);
}

// plantProfileSchema kept for importers expecting a default (SI) schema.
export const plantProfileSchema = makePlantProfileSchema("C", "mS/cm");

/** parseOptNum normalises a form-string field to number | undefined. */
export function parseOptNum(v: string | undefined): number | undefined {
  if (v === undefined || v === "") return undefined;
  const n = Number(v);
  return Number.isNaN(n) ? undefined : n;
}

export function emptyToUndef(s: string | undefined): string | undefined {
  return s && s.length > 0 ? s : undefined;
}

export interface PlantProfileFormProps {
  initial?: PlantProfile;
  submitting: boolean;
  onSubmit: (values: PlantProfileFormValues) => void;
  banner?: React.ReactNode;
  submitLabel?: string;
  showReason?: boolean;
}

export function PlantProfileForm({
  initial,
  submitting,
  onSubmit,
  banner,
  submitLabel,
  showReason = false,
}: PlantProfileFormProps) {
  const { temperatureUnit, ecUnit } = usePreferences();
  const form = useForm<PlantProfileFormValues>({
    resolver: zodResolver(makePlantProfileSchema(temperatureUnit, ecUnit)),
    defaultValues: defaultsFor(initial, temperatureUnit, ecUnit),
  });

  // Submit normalizes display units → storage (Celsius, mS/cm). To avoid
  // quantization drift, an EC/temp field the user did NOT edit is submitted as
  // its original stored value rather than re-derived from the rounded display
  // (see preserveOrConvert). pH isn't unit-converted, so it's untouched.
  const dirtyFields = form.formState.dirtyFields;
  const handleSubmit = (values: PlantProfileFormValues) => {
    const toC = (v: string | undefined) => displayToC(v, temperatureUnit);
    const toMs = (v: string | undefined) => displayToMsCm(v, ecUnit);
    onSubmit({
      ...values,
      temp_min_c: preserveOrConvert(values.temp_min_c, !!dirtyFields.temp_min_c, initial?.temp_min_c, toC),
      temp_ideal_c: preserveOrConvert(values.temp_ideal_c, !!dirtyFields.temp_ideal_c, initial?.temp_ideal_c, toC),
      temp_max_c: preserveOrConvert(values.temp_max_c, !!dirtyFields.temp_max_c, initial?.temp_max_c, toC),
      ec_min: preserveOrConvert(values.ec_min, !!dirtyFields.ec_min, initial?.ec_min, toMs),
      ec_ideal: preserveOrConvert(values.ec_ideal, !!dirtyFields.ec_ideal, initial?.ec_ideal, toMs),
      ec_max: preserveOrConvert(values.ec_max, !!dirtyFields.ec_max, initial?.ec_max, toMs),
    });
  };

  // AI lookup: ask the server to propose targets from the name/species, then
  // snapshot them into the form (converting to the display unit). Chemistry +
  // category/stage/feeder are filled outright; species/notes only when empty
  // so we never clobber what the user already typed.
  const suggest = useSuggestPlantProfile();
  const applySuggestion = (s: PlantProfileSuggestion) => {
    const nStr = (n?: number) => (n === undefined ? "" : String(n));
    const tStr = (c?: number) =>
      c === undefined ? "" : tempFromCelsius(c, temperatureUnit).toFixed(1);
    const eStr = (ms?: number) =>
      ms === undefined
        ? ""
        : ecUnit === "ppm700"
          ? String(Math.round(ecFromMsCm(ms, ecUnit)))
          : String(ms);
    // Applying a suggestion is an explicit edit, so mark fields dirty: submit
    // must convert these new display values rather than preserve the profile's
    // original stored value via the no-edit round-trip.
    const dirty = { shouldDirty: true } as const;
    if (s.category) form.setValue("category", s.category, dirty);
    if (s.stage) form.setValue("stage", s.stage, dirty);
    if (s.feeder_class) form.setValue("feeder_class", s.feeder_class, dirty);
    form.setValue("ph_min", nStr(s.ph_min), dirty);
    form.setValue("ph_ideal", nStr(s.ph_ideal), dirty);
    form.setValue("ph_max", nStr(s.ph_max), dirty);
    form.setValue("ec_min", eStr(s.ec_min), dirty);
    form.setValue("ec_ideal", eStr(s.ec_ideal), dirty);
    form.setValue("ec_max", eStr(s.ec_max), dirty);
    form.setValue("temp_min_c", tStr(s.temp_min_c), dirty);
    form.setValue("temp_ideal_c", tStr(s.temp_ideal_c), dirty);
    form.setValue("temp_max_c", tStr(s.temp_max_c), dirty);
    const cur = form.getValues();
    if (s.species && !cur.species) form.setValue("species", s.species);
    if (s.notes && !(cur.notes && cur.notes.length > 0))
      form.setValue("notes", s.notes);
  };
  const canLookup = !!(form.watch("name") || form.watch("species"));
  const lookup = () => {
    const { name, species } = form.getValues();
    if (!name && !species) return;
    suggest.mutate({ name, species }, { onSuccess: applySuggestion });
  };
  const lookupError =
    suggest.error instanceof ApiError
      ? suggest.error.code === "AI_UNAVAILABLE"
        ? "AI lookup isn’t configured on the server."
        : suggest.error.message
      : suggest.error
        ? "AI lookup failed."
        : null;

  return (
    <form
      onSubmit={form.handleSubmit(handleSubmit)}
      className="space-y-6 max-w-2xl"
      noValidate
    >
      {banner}

      <div className="flex items-center gap-3">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={!canLookup || suggest.isPending}
          onClick={lookup}
        >
          {suggest.isPending ? "Looking up…" : "✨ Look up with AI"}
        </Button>
        <span className="text-xs text-muted-foreground">
          Fills targets from the name/species below. Review before saving.
        </span>
      </div>
      {lookupError && <p className="text-xs text-destructive">{lookupError}</p>}

      <div className="grid grid-cols-2 gap-4">
        <Field label="Name" error={form.formState.errors.name?.message}>
          <Input
            placeholder="e.g. Tomato — Fruiting"
            {...form.register("name")}
            autoFocus
          />
        </Field>
        <Field label="Species" error={form.formState.errors.species?.message}>
          <Input placeholder="e.g. Tomato" {...form.register("species")} />
        </Field>
        <EnumSelect
          label="Category"
          name="category"
          form={form}
          options={PLANT_CATEGORY_OPTIONS}
          placeholder="— unset —"
        />
        <EnumSelect
          label="Stage"
          name="stage"
          form={form}
          options={PLANT_STAGE_OPTIONS}
          placeholder="— unset —"
        />
        <EnumSelect
          label="Feeder class"
          name="feeder_class"
          form={form}
          options={FEEDER_CLASS_OPTIONS}
          placeholder="— unset —"
        />
      </div>

      <fieldset className="space-y-3">
        <legend className="text-sm font-medium">Chemistry targets</legend>
        <RangeRow label="pH" name="ph" form={form} />
        <RangeRow
          label={`EC (${ecUnit === "ppm700" ? "ppm" : "mS/cm"})`}
          name="ec"
          form={form}
        />
        <RangeRow
          label={`Temp (${temperatureUnit})`}
          name="temp"
          form={form}
          suffix="_c"
        />
      </fieldset>

      <Field label="Notes">
        <Textarea rows={3} {...form.register("notes")} />
      </Field>

      {showReason && (
        <Field
          label="Reason for change"
          help="Captured in the audit log."
          error={form.formState.errors.reason?.message}
        >
          <Input {...form.register("reason")} />
        </Field>
      )}

      <div className="flex gap-2">
        <Button type="submit" disabled={submitting}>
          {submitting ? "Saving…" : (submitLabel ?? "Save")}
        </Button>
      </div>
    </form>
  );
}

function defaultsFor(
  p: PlantProfile | undefined,
  tempUnit: TemperatureUnit,
  ecUnit: ECUnit,
): PlantProfileFormValues {
  if (!p) {
    return { name: "", species: "", notes: "" };
  }
  const nStr = (n: number | undefined) => (n === undefined ? "" : String(n));
  const tStr = (c: number | undefined) =>
    c === undefined ? "" : tempFromCelsius(c, tempUnit).toFixed(1);
  const eStr = (ms: number | undefined) =>
    ms === undefined
      ? ""
      : ecUnit === "ppm700"
        ? String(Math.round(ecFromMsCm(ms, ecUnit)))
        : String(ms);
  return {
    name: p.name,
    species: p.species,
    category: p.category ?? "",
    stage: p.stage ?? "",
    feeder_class: p.feeder_class ?? "",
    ph_min: nStr(p.ph_min),
    ph_ideal: nStr(p.ph_ideal),
    ph_max: nStr(p.ph_max),
    ec_min: eStr(p.ec_min),
    ec_ideal: eStr(p.ec_ideal),
    ec_max: eStr(p.ec_max),
    temp_min_c: tStr(p.temp_min_c),
    temp_ideal_c: tStr(p.temp_ideal_c),
    temp_max_c: tStr(p.temp_max_c),
    notes: p.notes ?? "",
  };
}

function displayToC(
  v: string | undefined,
  unit: TemperatureUnit,
): string | undefined {
  if (v === undefined || v === "") return v;
  const n = Number(v);
  if (Number.isNaN(n)) return v;
  return tempToCelsius(n, unit).toFixed(2);
}

function displayToMsCm(
  v: string | undefined,
  unit: ECUnit,
): string | undefined {
  if (v === undefined || v === "") return v;
  const n = Number(v);
  if (Number.isNaN(n)) return v;
  return ecToMsCm(n, unit).toString();
}

function Field({
  label,
  help,
  error,
  children,
}: {
  label: string;
  help?: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1">
      <Label className="text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </Label>
      {children}
      {help && !error && (
        <p className="text-xs text-muted-foreground">{help}</p>
      )}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}

function RangeRow({
  label,
  name,
  form,
  suffix = "",
}: {
  label: string;
  name: "ph" | "ec" | "temp";
  form: UseFormReturn<PlantProfileFormValues>;
  suffix?: string;
}) {
  type Key = keyof PlantProfileFormValues;
  const minKey = (name + "_min" + suffix) as Key;
  const idealKey = (name + "_ideal" + suffix) as Key;
  const maxKey = (name + "_max" + suffix) as Key;
  const errors = form.formState.errors;
  // De-dup messages (an inverted range reports the same text on min + max).
  const messages = Array.from(
    new Set(
      [errors[minKey]?.message, errors[idealKey]?.message, errors[maxKey]?.message].filter(
        (m): m is string => typeof m === "string",
      ),
    ),
  );
  return (
    <div className="space-y-1">
      <div className="grid grid-cols-[80px_1fr_1fr_1fr] gap-3 items-center">
        <span className="text-xs text-muted-foreground">{label}</span>
        <Input type="number" step="any" placeholder="min" {...form.register(minKey)} />
        <Input type="number" step="any" placeholder="ideal" {...form.register(idealKey)} />
        <Input type="number" step="any" placeholder="max" {...form.register(maxKey)} />
      </div>
      {messages.map((m) => (
        <p key={m} className="text-xs text-destructive">
          {m}
        </p>
      ))}
    </div>
  );
}
